import {
  WebGLRenderTarget,
  LinearFilter,
  DepthTexture,
  UnsignedIntType,
  MeshBasicMaterial,
  Vector2,
  TextureLoader,
  RepeatWrapping,
  Mesh,
  Color,
  DoubleSide,
  ShaderMaterial,
  BackSide,
  Vector3,
  LineSegments,
  EdgesGeometry,
  LineBasicMaterial,
} from "three";

import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader.js";
import { outlineShader } from "../lib/outlineShader.js";
import { blitShader } from "../lib/blitShader.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass.js";

import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { Line2 } from "three/examples/jsm/lines/Line2.js";

export class OutlineControl {
  constructor(renderer, scene, camera, el) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.el = el;

    this.thickness = 4;
    this.outlineColor = 0x00ffff;

    // 0:正常边框高亮
    // 1：threejs后处理边框高亮
    // 2:获取模型线框（有角度限制），使用Line2加粗线显示，对于细碎的物体性能差（如很多树叶）
    // 3:颜色闪烁
    this.mode = 3;

    // 自发光设置
    this.emissiveColor1 = new Color(0x000000);
    this.emissiveColor2 = new Color(this.outlineColor);
    this.emissiveTime = 0;

    this._outlineSetting();
  }

  update(dt) {
    this.renderOutlineObjects();

    if (this.mode === 3 && this.sceneNode_SelectedObjects) {
      this.emissiveTime += dt * 2;
      const t = (Math.sin(this.emissiveTime) + 1) / 2; // 将正弦值映射到0-1范围
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; i++) {
        if (this.sceneNode_SelectedObjects[i].material.emissive == null) {
          continue;
        }
        this.sceneNode_SelectedObjects[i].material.emissive.lerpColors(
          this.emissiveColor1,
          this.emissiveColor2,
          t
        );
      }
      if (this.emissiveTime >= 1000) this.emissiveTime = 0;
    }
  }

  clear() {
    if (this.outlineComposer) this._disposeEffectComposer(this.outlineComposer);
    this.sceneNode_SelectedObjects = null;
    this.el = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.offlineRenderTarget1 = null;
    this.outlineComposer = null;
    this.outlineCopyPass = null;
    this.outlineFxaaPass = null;
    this.outlineBlendPass = null;

    super.clear();
  }

  _disposeEffectComposer(composer) {
    // 释放渲染目标
    composer.renderTarget1.dispose();
    composer.renderTarget2.dispose();

    // 释放 Passes
    composer.passes.forEach((pass) => {
      if (pass instanceof ShaderPass) {
        if (pass.uniforms[pass.textureID].value)
          pass.uniforms[pass.textureID].value.dispose(); // 释放 ShaderPass 的纹理
      }
      if (
        pass.renderToScreen === false &&
        pass.hasOwnProperty("renderTarget")
      ) {
        pass.renderTarget.dispose(); // 释放 Pass 的渲染目标
      }
    });

    // 释放 EffectComposer 本身
    composer = null;
  }

  /**
   * 切换相机，包括正交和透视相机
   * @param {*} camera
   */
  setCamera(camera) {
    this._disposeEffectComposer(this.outlineComposer);
    this.camera = camera;

    this._outlineSetting();
  }

  resize() {
    if (!this.el || !this.el.parentElement) return;
    let { clientHeight, clientWidth } = this.el.parentElement;

    this.outlineComposer.setSize(clientWidth, clientHeight);
    if (this.effectFXAA)
      this.effectFXAA.uniforms["resolution"].value.set(
        1 / clientWidth,
        1 / clientHeight
      );
  }

  _outlineSetting() {
    const { clientHeight, clientWidth } = this.el.parentElement;

    if (this.mode === 1) {
      // postprocessing

      this.outlineComposer = new EffectComposer(this.renderer);

      const renderPass = new RenderPass(this.scene, this.camera);
      this.outlineComposer.addPass(renderPass);

      this.outlinePass = new OutlinePass(
        new Vector2(clientWidth, clientHeight),
        this.scene,
        this.camera
      );
      this.outlineComposer.addPass(this.outlinePass);

      new TextureLoader().load("/assets/imgs/tri_pattern.jpg", (texture) => {
        this.outlinePass.patternTexture = texture;
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
      });

      this.outlinePass.edgeThickness = 1;
      this.outlinePass.visibleEdgeColor.set(this.outlineColor);

      this.effectFXAA = new ShaderPass(FXAAShader);
      this.effectFXAA.uniforms["resolution"].value.set(
        1 / clientWidth,
        1 / clientHeight
      );
      this.outlineComposer.addPass(this.effectFXAA);
    } else {
      this.MaxOfflineWidth = 800;
      this.offlineWidth = Math.min(this.MaxOfflineWidth, clientWidth);
      this.offlineHeight = (this.offlineWidth / clientWidth) * clientHeight;
      this.offlineRenderTarget1 = new WebGLRenderTarget(
        this.offlineWidth,
        this.offlineHeight
      );
      this.offlineRenderTarget1.texture.minFilter = LinearFilter;
      this.offlineRenderTarget1.texture.magFilter = LinearFilter;
      //this.offlineRenderTarget1.texture.generateMipmaps = true;
      this.offlineRenderTarget1.stencilBuffer = false;
      this.offlineRenderTarget1.depthBuffer = true;
      this.offlineRenderTarget1.depthTexture = new DepthTexture();
      this.offlineRenderTarget1.depthTexture.type = UnsignedIntType;

      this.outlineComposer = new EffectComposer(this.renderer);

      this.outlineCopyPass = new ShaderPass(blitShader);
      this.outlineCopyPass.clear = true;
      this.outlineCopyPass.material.uniforms["tTexture"].value =
        this.offlineRenderTarget1.texture;
      this.outlineComposer.addPass(this.outlineCopyPass);

      this.outlineFxaaPass = new ShaderPass(FXAAShader);
      var pixelRatio = this.renderer.getPixelRatio();
      this.outlineFxaaPass.material.uniforms["resolution"].value.x =
        1 / (this.offlineWidth * 0.8 * pixelRatio);
      this.outlineFxaaPass.material.uniforms["resolution"].value.y =
        1 / (this.offlineHeight * 0.8 * pixelRatio);
      this.outlineComposer.addPass(this.outlineFxaaPass);

      this.outlineBlendPass = new ShaderPass(outlineShader);
      this.outlineBlendPass.material.uniforms["screenSize"].value = new Vector2(
        clientWidth,
        clientHeight
      );
      this.outlineBlendPass.material.uniforms["outlineColor"].value = new Color(
        this.outlineColor
      );

      this.outlineBlendPass.material.transparent = true;
      this.outlineComposer.addPass(this.outlineBlendPass);
    }
  }

  /**
   * 渲染边框
   */
  renderOutlineObjects() {
    if (this.mode !== 0 && this.mode !== 1) return;

    if (
      !this.sceneNode_SelectedObjects ||
      this.sceneNode_SelectedObjects.length <= 0
    ) {
      return;
    }

    if (this.mode === 1) {
      this.outlineComposer.render();
    } else {
      const clearColor = this.renderer.getClearColor(new Color());
      this.renderer.setClearColor("#000000");
      let hasSkybox = false;
      let scene = this.scene;
      let skymap = null;
      if (scene.background) {
        skymap = scene.background;
        scene.background = null;

        hasSkybox = true;
      }

      // Prepare specific layer rendering
      var outlineLayer = 343; // Or some other value
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; ++i) {
        this.sceneNode_SelectedObjects[i].layers.set(outlineLayer);
      }

      this.camera.layers.set(outlineLayer);

      // Render color buffer
      this.renderer.setRenderTarget(this.offlineRenderTarget1);

      //this.renderer.clearColor = new Color
      this.renderer.clear();

      scene.overrideMaterial = new MeshBasicMaterial({
        color: new Color(0xffffff),
        side: DoubleSide,
      });

      this.renderer.render(scene, this.camera);

      // Render outline
      this.renderer.setRenderTarget(null);

      this.outlineComposer.render();

      // Restore outline object layers
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; ++i) {
        this.sceneNode_SelectedObjects[i].layers.set(0);
      }

      // Restore render states
      scene.overrideMaterial = null;

      this.camera.layers.set(0);

      this.renderer.setRenderTarget(null);
      this.renderer.setClearColor(clearColor, 1);
      if (hasSkybox && skymap) scene.background = skymap;
    }
  }

  /**
   * 将选中的物体添加外边框
   * @param {选中的物体}} object
   */
  setOutlineObjects(object) {
    this._cancleOutline();

    if (object && object.type != "Sprite") {
      let objects = object;
      if (!Array.isArray(object)) {
        objects = [object];
      }

      for (var i = 0; i < objects.length; i++) {
        object = objects[i];

        if (object.isMesh) this.sceneNode_SelectedObjects.push(object);
        else {
          object.traverse((node) => {
            if (node instanceof Mesh) {
              if (node.type !== "Sprite") {
                this.sceneNode_SelectedObjects.push(node);
              }
            }
          });
        }
      }
    }
    if (this.mode === 1) {
      if (
        !this.sceneNode_SelectedObjects ||
        this.sceneNode_SelectedObjects.length <= 0
      ) {
        this.outlinePass.selectedObjects = [];
      } else {
        this.outlinePass.selectedObjects = this.sceneNode_SelectedObjects;
      }
    } else if (this.mode === 2) {
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; i++) {
        var obj = this.sceneNode_SelectedObjects[i];
        const cubeEdges = new EdgesGeometry(obj.geometry, 60);
        const geometry = new LineSegmentsGeometry().fromEdgesGeometry(
          cubeEdges
        );
        const copyObj = new Line2(geometry, this.lineMaterial);
        copyObj.applyMatrix4(obj.matrixWorld);
        this.scene.add(copyObj);
        this.copyObj.push(copyObj);
      }
    } else if (this.mode === 3) {
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; i++) {
        var obj = this.sceneNode_SelectedObjects[i];
        obj.userData._srcOutlineMat = obj.material;
        obj.material = obj.material.clone();
      }
    }
  }

  /**
   * 取消高亮
   * @returns
   */
  _cancleOutline() {
    if (!this.sceneNode_SelectedObjects) {
      this.sceneNode_SelectedObjects = [];
      this.copyObj = []; // 存放法线外扩的对象
      this._createOutlightMat();
      return;
    }

    if (this.mode === 2 && this.copyObj) {
      for (var i = 0; i < this.copyObj.length; i++) {
        this.copyObj[i].geometry.dispose();
        this.copyObj[i].removeFromParent();
      }
      this.copyObj = [];
    }

    if (this.mode === 3) {
      for (var i = 0; i < this.sceneNode_SelectedObjects.length; i++) {
        var obj = this.sceneNode_SelectedObjects[i];
        if (obj.userData._srcOutlineMat) {
          obj.material.dispose();
          obj.material = obj.userData._srcOutlineMat;
          delete obj.userData._srcOutlineMat;
        }
      }
    }

    if (this.sceneNode_SelectedObjects.length > 0) {
      this.sceneNode_SelectedObjects = [];
    }
  }

  /**
   * 切换模式  0跟1之间不能切换，0<->2、0<->3、1<->2、1<->3
   * @param {*} mode
   */
  changeMode(mode) {
    this._cancleOutline();
    if (mode >= 0 && mode <= 3) this.mode = mode;
  }

  _createOutlightMat() {
    this.lineMaterial = new LineMaterial({
      color: this.outlineColor,
      linewidth: this.thickness,
    });
    this.lineMaterial.resolution.set(window.innerWidth, window.innerHeight);
  }
}
